import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 브라우저 뒤로가기로 modal / overlay 를 닫는 history 통합 hook.
 *
 * modal 이 열릴 때 현재 URL 과 동일한 history 항목을 pushState 로 쌓고(주소는 byte 단위로
 * 불변, route 변경 없음), 뒤로가기(popstate) 시 페이지 이탈 대신 최상단 modal 만 닫는다.
 * 프로그램적 닫기(X / 취소 / 확인)는 top 항목일 때 history.back() 으로 항목을 pop 해
 * 화면 상태와 history 스택을 정합 상태로 유지한다.
 *
 * raw window.history 위에 전역 LIFO 스택을 둔다. react-router 의 location.state 는
 * clone / merge 되어 stacking·navigate-away 추론이 복잡하므로 사용하지 않는다.
 */

const HISTORY_KEY = '__np_modal__';

interface ModalHistoryState {
  [HISTORY_KEY]?: { stack: number[] };
}

/** 전역 LIFO 스택 — 현재 열려 있는 modal id 들 (open 순서) */
const stack: number[] = [];
let nextId = 1;

/** 각 modal id 의 닫기 핸들러 */
const closers = new Map<number, () => void>();

let popstateBound = false;
// 프로그램적 history.back() 으로 발생한 popstate 는 무시 (사용자 뒤로가기와 구분).
let ignoreNextPopstate = false;

function handlePopstate() {
  if (ignoreNextPopstate) {
    ignoreNextPopstate = false;
    return;
  }
  // 사용자 뒤로가기 — 우리 스택 top modal 을 닫는다.
  const top = stack[stack.length - 1];
  if (top === undefined) return;
  const onClose = closers.get(top);
  // top sentinel 항목은 브라우저가 이미 pop. 스택에서 제거 후 닫기 콜백 호출.
  stack.pop();
  closers.delete(top);
  onClose?.();
}

function ensurePopstateBound() {
  if (popstateBound || typeof window === 'undefined') return;
  window.addEventListener('popstate', handlePopstate);
  popstateBound = true;
}

function pushEntry(id: number) {
  ensurePopstateBound();
  stack.push(id);
  const prev = (window.history.state ?? {}) as ModalHistoryState;
  // 현재 URL 그대로 push — 주소 불변, 뒤로가기 가능한 sentinel 항목 생성.
  window.history.pushState(
    { ...prev, [HISTORY_KEY]: { stack: [...stack] } },
    '',
    window.location.href,
  );
}

/**
 * 프로그램적 닫기(X / 취소 / 확인) 또는 unmount 시 항목 정리.
 *
 * 현재 history 항목이 우리 sentinel 이고 이 id 가 그 top 일 때만 history.back() 으로 pop.
 * (1) 사용자가 다른 route 로 이동 후 unmount → 현재 항목은 router 의 것이므로 back() 안 함
 *     (정당한 네비게이션에서 튕기지 않도록). (2) 중첩 중 비 top 이 먼저 닫힘 → 스택만 정리.
 */
function popEntry(id: number) {
  const idx = stack.indexOf(id);
  if (idx === -1) return;
  closers.delete(id);
  stack.splice(idx, 1);

  const sentinel = (window.history.state as ModalHistoryState | null)?.[HISTORY_KEY];
  const historyTop = sentinel?.stack[sentinel.stack.length - 1];
  if (historyTop === id) {
    ignoreNextPopstate = true;
    window.history.back();
  }
}

/**
 * boolean modal 상태에 뒤로가기-닫기 동작을 부착하는 최소 diff 브리지.
 *
 * @param isOpen  modal 열림 여부
 * @param onClose 닫기 콜백 (X / 취소 / 확인 / 뒤로가기 공통 진입점)
 */
export function useHistoryBackClose(isOpen: boolean, onClose: () => void): void {
  const idRef = useRef<number | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    // idempotent — StrictMode 등 중복 effect 시 항목 1개만 push.
    if (idRef.current !== null) return;
    const id = nextId++;
    idRef.current = id;
    closers.set(id, () => onCloseRef.current());
    pushEntry(id);

    return () => {
      const id = idRef.current;
      idRef.current = null;
      if (id === null) return;
      // 닫힘(isOpen=false) 또는 unmount.
      // 스택에 남아 있으면(=뒤로가기로 닫힌 게 아니라 프로그램적/언마운트) 항목 정리.
      if (stack.includes(id)) {
        popEntry(id);
      } else {
        closers.delete(id);
      }
    };
  }, [isOpen]);
}

interface HistoryModal {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

/**
 * 자체적으로 open 상태를 소유하는 modal hook. 뒤로가기-닫기가 내장된다.
 *
 * const modal = useHistoryModal();
 * <button onClick={modal.open}>열기</button>
 * <Modal isOpen={modal.isOpen} onClose={modal.close} />
 */
export function useHistoryModal(initialOpen = false): HistoryModal {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  useHistoryBackClose(isOpen, close);
  return { isOpen, open, close };
}

export default useHistoryBackClose;
